import Decimal from 'decimal.js';


const numberWords = [
  'không', 'một', 'hai', 'ba', 'bốn',
  'năm', 'sáu', 'bảy', 'tám', 'chín'
];

function readTriple(number: number): string {
  const hundreds = Math.floor(number / 100);
  const tens = Math.floor((number % 100) / 10);
  const units = number % 10;
  let result = '';

  if (hundreds !== 0) {
    result += numberWords[hundreds] + ' trăm';
  }

  if (tens === 0 && units !== 0) {
    result += (hundreds !== 0 ? ' lẻ' : '') + ' ' + numberWords[units];
  } else if (tens === 1) {
    result += ' mười';
    if (units === 5) result += ' lăm';
    else if (units !== 0) result += ' ' + numberWords[units];
  } else if (tens > 1) {
    result += ' ' + numberWords[tens] + ' mươi';
    if (units === 1) result += ' mốt';
    else if (units === 5) result += ' lăm';
    else if (units !== 0) result += ' ' + numberWords[units];
  } else if (hundreds === 0 && units !== 0) {
    result += numberWords[units];
  }

  return result.trim();
}


class NumHelper {
  private value: Decimal;

  constructor(...numbers: (number | string | null)[]) {
    if (numbers.length === 0 || !numbers) {
      this.value = new Decimal(0);
    } else {
      if (!numbers[0]) this.value = new Decimal(0);
      else {
        // Start with the first number instead of 0 to avoid extra addition
        this.value = new Decimal(numbers[0]);
        // Sum remaining numbers if any
        if (numbers.length > 1) {
          this.value = numbers
            .slice(1)
            .reduce((acc, num) => acc.plus(new Decimal(num || 0)), this.value);
        }
      }
    }
  }

  plus(...numbers: (number | string)[]): this {
    numbers.forEach((num) => {
      this.value = this.value.plus(new Decimal(num));
    });
    return this;
  }

  minus(...numbers: (number | string)[]): this {
    numbers.forEach((num) => {
      this.value = this.value.minus(new Decimal(num));
    });
    return this;
  }

  times(...numbers: (number | string)[]): this {
    if (numbers.length === 0) return this;

    numbers.forEach((num) => {
      this.value = this.value.times(new Decimal(num));
    });
    return this;
  }

  divide(...numbers: (number | string)[]): this {
    if (numbers.length === 0) return this;

    numbers.forEach((num) => {
      const decimal = new Decimal(num);
      if (decimal.isZero()) {
        throw new Error('Cannot divide by zero');
      }
      this.value = this.value.dividedBy(decimal);
    });
    return this;
  }

  exchange(rate: number | string): this {
    const decimalRate = new Decimal(rate);
    if (decimalRate.isNegative()) {
      throw new Error('Exchange rate cannot be negative');
    }
    this.value = this.value.times(decimalRate);
    return this;
  }

  format(
    decimalPlaces: number = 0,
    decimalSeparator: string = ',',
    thousandSeparator: string = '.',
  ): string {
    if (decimalPlaces < 0) {
      throw new Error('Decimal places cannot be negative');
    }

    // Làm tròn số đến số chữ số thập phân yêu cầu
    const roundedValue = this.value.toDecimalPlaces(decimalPlaces);

    // Convert sang string với định dạng cố định
    const numberStr = roundedValue.toFixed(decimalPlaces);

    // Tách phần nguyên và phần thập phân
    const [integerPart, decimalPart] = numberStr.split('.');

    // Format phần nguyên với dấu phân cách hàng nghìn
    const formattedInteger = integerPart.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      thousandSeparator,
    );

    // Nếu không yêu cầu chữ số thập phân, trả về phần nguyên đã format
    if (decimalPlaces === 0) {
      return formattedInteger;
    }

    // Nếu có yêu cầu chữ số thập phân, ghép với phần nguyên
    return `${formattedInteger}${decimalSeparator}${decimalPart}`;
  }

  toNumber(): number {
    return this.value.toNumber();
  }

  toString(): string {
    return this.value.toString();
  }

  // Trả về giá trị làm tròn thành số nguyên
  getIntValue(): number {
    return Math.round(this.value.toNumber());
  }
  getFloatValue(): number {
    return Math.round(this.value.toNumber() * 100) / 100;
  }

  // Trả về giá trị số nguyên dưới dạng string
  getIntValueAsString(round: boolean = true): string {
    return round ? Math.round(this.value.toNumber()).toString() : this.value.toString();
  }

  // Trả về giá trị Decimal đã làm tròn
  getRoundedValue(): Decimal {
    return new Decimal(Math.round(this.value.toNumber()));
  }

  // Phương thức cũ getValue() giữ nguyên để tương thích ngược
  getValue(): Decimal {
    return this.value;
  }

  // Làm tròn giá trị hiện tại thành số nguyên và trả về this để có thể chain
  round(): this {
    this.value = new Decimal(Math.round(this.value.toNumber()));
    return this;
  }

  isEqual(paid: number | string): boolean {
    return this.value.equals(new Decimal(paid));
  }

  isGreaterThan(paid: number | string): boolean {
    return this.value.greaterThan(new Decimal(paid));
  }

  isGreaterThanOrEqualTo(paid: number | string): boolean {
    return this.value.greaterThanOrEqualTo(new Decimal(paid));
  }

  isLessThan(paid: number | string): boolean {
    return this.value.lessThan(new Decimal(paid));
  }

  isLessThanOrEqualTo(paid: number | string): boolean {
    return this.value.lessThanOrEqualTo(new Decimal(paid));
  }

  numberToVietnameseText(config: object | undefined = undefined): string {
    let n: number|NaN = this.value || NaN;
    if (isNaN(n) || !isFinite(n)) return '';
    if (n === 0) return 'Không';

    const unitNames = ['', 'nghìn', 'triệu', 'tỷ'];
    const parts: string[] = [];
    let negative = false;

    if (n < 0) {
      negative = true;
      n = Math.abs(n);
    }

    let i = 0;
    while (n > 0) {
      const part = n % 1000;
      if (part !== 0) {
        const text = readTriple(part);
        const suffix = unitNames[i] ? ' ' + unitNames[i] : '';
        parts.unshift(text + suffix);
      } else if (parts.length > 0 && i > 0) {
        // Giữ số 0 giữa các đơn vị để đọc đúng ngữ cảnh: "một tỷ không trăm..."
        parts.unshift('không');
      }
      n = Math.floor(n / 1000);
      i++;
    }

    const result = (negative ? 'Âm ' : '') + parts.join(' ').replace(/\s+/g, ' ').trim();
    const text = result.charAt(0).toUpperCase() + result.slice(1);
    return text ? text +  ' ' +(config?.unit || '') : '';
  }
}

export const numHelper = (...numbers: (number | string)[]) =>
  new NumHelper(...numbers);
